import React from "react";
import api from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useOrg } from "../../context/OrgContext";
import { useTerminology } from "../../context/TerminologyContext";
import PinModal from "../../components/PinModal";
import LocalPdfPreview from "../../components/LocalPdfPreview";
import {
  AlertModal,
  ChecklistHeaderTable,
  LotSelectionTable,
  ModalShell,
  ReqLabel,
  isChecklistMonthLabel,
  normalizeChecklistHeaderLabel,
} from "../../components/ui-helpers";

const cleanValue = (value) => String(value ?? "").trim();
const firstValue = (source, keys) => {
  for (const key of keys) {
    const value = cleanValue(source?.[key]);
    if (value) return value;
  }
  return "";
};
const LOCKED_CHECKLIST_LABELS = [
  "Product Name",
  "Pack Size",
  "Batch No",
  "Batch No.",
];
const applyLockedChecklistValues = (
  template,
  currentValues,
  { material, sku, lot },
) => {
  const next = { ...(currentValues || {}) };
  (template?.header?.pairs || []).flat().forEach((label) => {
    const key = normalizeChecklistHeaderLabel(label);
    if (key === "productname") next[label] = material || "";
    if (key === "packsize") next[label] = sku || "";
    if (key === "batchno") next[label] = lot || "";
  });
  return next;
};
const shallowSame = (a, b) => {
  const ak = Object.keys(a || {});
  const bk = Object.keys(b || {});
  if (ak.length !== bk.length) return false;
  return ak.every((key) => String(a?.[key] ?? "") === String(b?.[key] ?? ""));
};

export default function UploadCoaPage() {
  const { user } = useAuth();
  const { current: currentOrg } = useOrg();
  const { t } = useTerminology();
  const vname = user?.uid || "";

  const [checklistEnabled, setChecklistEnabled] = React.useState(true);
  const [materials, setMaterials] = React.useState([]);
  const [material, setMaterial] = React.useState("");
  const [sku, setSku] = React.useState("");
  const [materialId, setMaterialId] = React.useState("");
  const [lot, setLot] = React.useState("");
  const [file, setFile] = React.useState(null);
  const [template, setTemplate] = React.useState(null);
  const [headerVals, setHeaderVals] = React.useState({});
  const [checkPdf, setCheckPdf] = React.useState(null);
  const [qOpen, setQOpen] = React.useState(false);
  const [qAns, setQAns] = React.useState("N/A");
  const [qDev, setQDev] = React.useState("");
  const [pinOpen, setPinOpen] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState(null);
  const [alert, setAlert] = React.useState({ open: false, msg: "", title: "" });
  const [materialTableOpen, setMaterialTableOpen] = React.useState(true);

  const coaFileRef = React.useRef(null);
  const checklistFileRef = React.useRef(null);
  const lotInputRef = React.useRef(null);

  const showAlert = React.useCallback((msg, title = "Notice") => {
    setAlert({ open: true, msg, title });
  }, []);

  React.useEffect(() => {
    api
      .get("/orgs/settings")
      .then((response) =>
        setChecklistEnabled(!!response.data?.checklist_enabled),
      )
      .catch(() => setChecklistEnabled(true));
  }, [currentOrg]);

  React.useEffect(() => {
    if (!vname) return;
    api
      .get(`/list_vendor_materials_approved/${encodeURIComponent(vname)}`)
      .then((response) => setMaterials(response.data.materials || []))
      .catch(() => setMaterials([]));
  }, [vname, currentOrg]);

  React.useEffect(() => {
    setTemplate(null);
    setHeaderVals({});
    setQAns("N/A");
    setQDev("");
    setCheckPdf(null);
    if (checklistFileRef.current) checklistFileRef.current.value = "";

    if (!vname || !sku || !checklistEnabled) return;
    api
      .get("/checklists/template", {
        params: {
          vendor: vname,
          sku,
          ...(materialId ? { material_id: materialId } : {}),
        },
      })
      .then((response) =>
        setTemplate(response.data?.enabled ? response.data : null),
      )
      .catch(() => setTemplate(null));
  }, [vname, sku, materialId, checklistEnabled, currentOrg]);

  React.useEffect(() => {
    if (!template) return;
    setHeaderVals((current) => {
      const next = applyLockedChecklistValues(template, current, {
        material,
        sku,
        lot,
      });
      return shallowSame(current, next) ? current : next;
    });
  }, [template, material, sku, lot]);

  const materialTableRows = React.useMemo(
    () =>
      (materials || []).map((row, index) => {
        const materialValue = cleanValue(row.material);
        const skuValue = cleanValue(row.sku);
        const materialIdValue = cleanValue(row.material_id);
        const category = firstValue(row, [
          "type",
          "category",
          "checklist_type",
          "checklist_category",
          "threshold_type",
        ]);
        return {
          ...row,
          material: materialValue,
          sku: skuValue,
          material_id: materialIdValue,
          checklist_category: category || "—",
          _rowKey: materialIdValue
            ? `mid:${materialIdValue}`
            : `legacy:${index}:${materialValue}:${skuValue}:${category}`,
        };
      }),
    [materials],
  );

  const selectedMaterialRowId = React.useMemo(() => {
    if (!material && !sku && !materialId) return "";
    const match = materialTableRows.find((row) => {
      if (materialId)
        return cleanValue(row.material_id) === cleanValue(materialId);
      return (
        cleanValue(row.material) === cleanValue(material) &&
        cleanValue(row.sku) === cleanValue(sku)
      );
    });
    return match?._rowKey || "";
  }, [materialTableRows, material, sku, materialId]);

  const selectMaterialRow = React.useCallback((row) => {
    setMaterial(cleanValue(row.material));
    setSku(cleanValue(row.sku));
    setMaterialId(cleanValue(row.material_id));
    setMaterialTableOpen(false);
  }, []);

  const materialResolutionMessage =
    material && sku && !materialId
      ? `No ${t("material").toLowerCase()} ID is available for this ${t("material")} + SKU yet; legacy upload will be attempted.`
      : "";

  const checklistLabels = React.useMemo(
    () =>
      (template?.header?.pairs || []).flatMap((pair) =>
        pair.filter((label) => String(label || "").trim() !== ""),
      ),
    [template],
  );

  const isIntField = React.useCallback((label) => {
    const key = normalizeChecklistHeaderLabel(label);
    return ["enteryes", "enterno", "enterna", "y", "n", "na"].includes(key);
  }, []);

  const isFloatField = React.useCallback((label) => {
    const key = String(label || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .trim();
    return (
      key === "finalyield" ||
      key === "mrp" ||
      key === "mrprs" ||
      key === "mrprs"
    );
  }, []);

  const normalizeDecimal = React.useCallback((value) => {
    const cleaned = String(value ?? "")
      .replace(/[^\d.]/g, "")
      .replace(/(\..*)\./g, "$1");
    const match = cleaned.match(/^\d*(?:\.\d{0,3})?/);
    return match ? match[0] : "";
  }, []);

  const updateHeaderValue = React.useCallback((label, value) => {
    const key = normalizeChecklistHeaderLabel(label);
    if (["productname", "packsize", "batchno"].includes(key)) return;
    setHeaderVals((current) => ({ ...current, [label]: value }));
  }, []);

  const resetUploadState = React.useCallback((preserveSelection = true) => {
    setLot("");
    setFile(null);
    setCheckPdf(null);
    setHeaderVals({});
    setQAns("N/A");
    setQDev("");
    setQOpen(false);
    if (!preserveSelection) {
      setMaterial("");
      setSku("");
      setMaterialId("");
      setTemplate(null);
    }
    if (coaFileRef.current) coaFileRef.current.value = "";
    if (checklistFileRef.current) checklistFileRef.current.value = "";
    window.setTimeout(() => lotInputRef.current?.focus(), 30);
  }, []);

  const validateChecklistRequired = React.useCallback(() => {
    if (!checklistEnabled) return "";
    if (!template) return "Checklist template not loaded.";
    const missing = checklistLabels.filter(
      (label) => !String(headerVals[label] ?? "").trim(),
    );
    if (missing.length) return `Please fill: ${missing.join(", ")}`;
    if ((qAns || "").toUpperCase() === "Y" && !String(qDev || "").trim()) {
      return template?.question?.extra_label || "Deviation No. is required.";
    }
    return "";
  }, [checklistEnabled, template, checklistLabels, headerVals, qAns, qDev]);

  const submitUpload = React.useCallback(async () => {
    if (!vname || !material || !sku || !lot || !file) {
      showAlert(
        `${t("material")}, SKU, ${t("lot").toLowerCase()} and COA PDF are required.`,
        "Validation",
      );
      return;
    }
    const err = validateChecklistRequired();
    if (err) {
      showAlert(err, "Missing fields");
      return;
    }

    const form = new FormData();
    form.append("vendor_name", vname);
    form.append("sku", sku);
    if (materialId) form.append("material_id", materialId);
    form.append("lot_no", lot);
    form.append("file", file);

    if (checklistEnabled && template) {
      if (checkPdf) form.append("checklist", checkPdf);
      form.append("header_json", JSON.stringify(headerVals));
      form.append("q_answer", qAns);
      form.append("q_deviation_no", qDev);
    }

    try {
      await api.post("/vendor/queue_add", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showAlert(
        "Successfully submitted. The next upload can start immediately.",
        "Success",
      );
      resetUploadState(true);
    } catch (error) {
      showAlert(error?.response?.data?.error || "Could not submit.", "Error");
    }
  }, [
    vname,
    material,
    sku,
    materialId,
    lot,
    file,
    checklistEnabled,
    template,
    checkPdf,
    headerVals,
    qAns,
    qDev,
    showAlert,
    validateChecklistRequired,
    resetUploadState,
    t,
  ]);

  const openPinFlow = React.useCallback(() => {
    if (!vname || !material || !sku || !lot || !file) {
      showAlert(
        `${t("material")}, SKU, ${t("lot").toLowerCase()} and COA PDF are required.`,
        "Validation",
      );
      return;
    }
    const err = validateChecklistRequired();
    if (err) {
      showAlert(err, "Missing fields");
      return;
    }

    setPendingAction(() => submitUpload);
    setPinOpen(true);
  }, [
    vname,
    material,
    sku,
    lot,
    file,
    showAlert,
    validateChecklistRequired,
    submitUpload,
    t,
  ]);

  const startSubmit = React.useCallback(() => {
    if (!vname || !material || !sku || !lot || !file) {
      showAlert(
        `${t("material")}, SKU, ${t("lot").toLowerCase()} and COA PDF are required.`,
        "Validation",
      );
      return;
    }
    if (checklistEnabled && template?.question?.text) {
      const err = validateChecklistRequired();
      if (err) {
        showAlert(err, "Missing fields");
        return;
      }
      setQOpen(true);
      return;
    }
    openPinFlow();
  }, [
    vname,
    material,
    sku,
    lot,
    file,
    checklistEnabled,
    template,
    validateChecklistRequired,
    openPinFlow,
    showAlert,
    t,
  ]);

  return (
    <div className="section vendor-upload-shell">
      <h2>Upload one COA PDF</h2>
      <div className="helper-text" style={{ marginBottom: 12 }}>
        This page is for fresh {t("lot").toLowerCase()} only. Re-uploads for
        invalidated {t("lot").toLowerCase()} are available under{" "}
        <strong>Notifications</strong>.
      </div>

      {materials.length === 0 ? (
        <div>No approved {t("material").toLowerCase()} yet.</div>
      ) : (
        <>
          <div className="form-grid vendor-upload-grid">
            <div className="field span-12">
              <details
                className="selection-expander"
                open={materialTableOpen}
                onToggle={(event) =>
                  setMaterialTableOpen(event.currentTarget.open)
                }
              >
                <summary>Select {t("material")} and SKU</summary>
                <div className="helper-text" style={{ marginBottom: 8 }}>
                  Choose the approved {t("material").toLowerCase()}/SKU row for
                  this COA.
                </div>
                <LotSelectionTable
                  rows={materialTableRows}
                  columns={[
                    { key: "material", label: t("material") },
                    { key: "sku", label: "SKU" },
                    {
                      key: "checklist_category",
                      label: `Checklist ${t("type")}`,
                    },
                  ]}
                  sortOptions={[
                    { key: "material", label: t("material") },
                    { key: "sku", label: "SKU" },
                    {
                      key: "checklist_category",
                      label: `Checklist ${t("type")}`,
                    },
                  ]}
                  selected={selectedMaterialRowId}
                  onSelectionChange={(_id, row) => selectMaterialRow(row)}
                  getRowId={(row) => row._rowKey}
                  searchPlaceholder={`Search ${t("material").toLowerCase()}, SKU, or checklist ${t("type").toLowerCase()}`}
                  emptyText={`No approved ${t("material").toLowerCase()} yet.`}
                  pageSize={5}
                  className="vendor-upload-material-table"
                />
              </details>
              {materialResolutionMessage ? (
                <div className="helper-text error-text">
                  {materialResolutionMessage}
                </div>
              ) : null}
            </div>
            <div className="field span-4">
              <ReqLabel>{t("lot")}</ReqLabel>
              <input
                ref={lotInputRef}
                value={lot}
                onChange={(event) => setLot(event.target.value)}
              />
            </div>

            <div className="field span-6 checklist-upload-field">
              <ReqLabel>COA PDF</ReqLabel>
              <div className="file-preview-row">
                <input
                  ref={coaFileRef}
                  className="premium-file-input"
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
                <LocalPdfPreview file={file} title="COA PDF" />
              </div>
            </div>

            {checklistEnabled && template ? (
              <>
                <div className="field span-12 checklist-header-shell">
                  <label>Checklist header</label>
                  <ChecklistHeaderTable
                    template={template}
                    values={headerVals}
                    onChange={updateHeaderValue}
                    isMonthField={isChecklistMonthLabel}
                    isIntField={isIntField}
                    isFloatField={isFloatField}
                    normalizeDecimal={normalizeDecimal}
                    lockedLabels={LOCKED_CHECKLIST_LABELS}
                  />
                </div>
                <div className="field span-6 checklist-upload-field">
                  <ReqLabel>Checklist PDF (filled)</ReqLabel>
                  <div className="file-preview-row">
                    <input
                      ref={checklistFileRef}
                      className="premium-file-input"
                      type="file"
                      accept="application/pdf"
                      onChange={(event) =>
                        setCheckPdf(event.target.files?.[0] || null)
                      }
                    />
                    <LocalPdfPreview file={checkPdf} title="Checklist PDF" />
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <div className="card-actions" style={{ marginTop: 14 }}>
            <button
              className="btn btn-primary"
              type="button"
              onClick={startSubmit}
            >
              Submit for Processing
            </button>
          </div>

          <ModalShell
            open={qOpen}
            onClose={() => setQOpen(false)}
            title="Checklist confirmation"
            subtitle={`Confirm the checklist question before this ${t("lot").toLowerCase()} is queued for processing.`}
            size="sm"
            actions={
              <>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setQOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    setQOpen(false);
                    openPinFlow();
                  }}
                >
                  Confirm &amp; Submit
                </button>
              </>
            }
          >
            <div className="page-stack">
              <div>
                <strong>Question:</strong> {template?.question?.text}{" "}
                {template?.question?.code ? (
                  <small>({template.question.code})</small>
                ) : null}
              </div>
              <div className="hstack" style={{ gap: 12, flexWrap: "wrap" }}>
                <div className="field" style={{ minWidth: 180 }}>
                  <ReqLabel>Answer</ReqLabel>
                  <select
                    value={qAns}
                    onChange={(event) => setQAns(event.target.value)}
                  >
                    <option>N/A</option>
                    <option>Y</option>
                    <option>N</option>
                  </select>
                </div>
                {qAns === "Y" ? (
                  <div className="field" style={{ flex: "1 1 220px" }}>
                    <ReqLabel>
                      {template?.question?.extra_label || "Deviation No."}
                    </ReqLabel>
                    <input
                      value={qDev}
                      onChange={(event) => setQDev(event.target.value)}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </ModalShell>

          <PinModal
            uid={user?.uid}
            open={pinOpen}
            onClose={() => setPinOpen(false)}
            onVerified={async () => {
              setPinOpen(false);
              if (pendingAction) await pendingAction();
            }}
          />

          <AlertModal
            open={alert.open}
            title={alert.title}
            message={alert.msg}
            onClose={() => setAlert({ open: false, msg: "", title: "" })}
          />
        </>
      )}
    </div>
  );
}
